import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';

const HeroSection = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    { market: 'Probable', color: '#a78bfa' },
    { market: 'Polymarket', color: '#f2e2a6' },
    { market: 'Opinion', color: '#60a5fa' }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative bg-gradient-to-br from-[#06070d] via-[#0b0e1a] to-[#06070d] overflow-hidden">
      <Navbar />
      
      {/* Céu estrelado: três camadas de pontos em tamanhos diferentes dão
          profundidade sem custar uma imagem. */}
      <div className="absolute inset-0" style={{
        backgroundImage: [
          'radial-gradient(1px 1px at 25px 35px, rgba(255,255,255,0.7) 50%, transparent 51%)',
          'radial-gradient(1px 1px at 135px 95px, rgba(242,226,166,0.6) 50%, transparent 51%)',
          'radial-gradient(1.5px 1.5px at 75px 160px, rgba(255,255,255,0.4) 50%, transparent 51%)',
          'radial-gradient(1px 1px at 190px 55px, rgba(255,255,255,0.35) 50%, transparent 51%)'
        ].join(', '),
        backgroundSize: '220px 220px'
      }} />

      {/* Hero content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 md:pt-32 pb-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Text content */}
          <div className="space-y-8">
            {/* "Polymarket" é o nome mais longo dos três — renderizado
                invisível, na mesma classe, ele força o h1 a ter sempre a
                altura do pior caso (2 ou 3 linhas conforme a largura real
                do ecrã), sem adivinhar um min-h em rem que acerta num
                breakpoint e falha noutro. O texto visível fica por cima,
                em absolute, sem afectar essa altura. */}
            <h1 className="relative text-5xl md:text-7xl font-bold leading-tight">
              <span className="invisible" aria-hidden="true">
                Arbitrage<br />across Polymarket
              </span>
              <span className="absolute inset-0">
                <span className="text-white">Arbitrage</span>
                <br />
                <span className="text-white">across </span>
                <span
                  className="inline-block whitespace-nowrap transition-colors duration-500"
                  style={{ color: slides[currentSlide].color }}
                >
                  {slides[currentSlide].market}
                </span>
              </span>
            </h1>

            <p className="text-gray-400 text-xl max-w-lg">
              moon.land scans prediction markets for spread opportunities, around the clock. Land your edge with live data and AI-annotated signals.
            </p>

            <div className="flex flex-wrap gap-4">
              <Button 
                onClick={() => {
                  import('../lib/analytics').then(m => m.analytics.ctaClick('hero_launch_terminal'));
                  navigate('/terminal');
                }}
                className="bg-[#f2e2a6] text-black hover:bg-[#f2e2a6]/90 font-semibold px-8 py-6 text-lg transition-all hover:scale-105"
                data-testid="hero-launch-terminal"
              >
                <ExternalLink className="w-5 h-5 mr-2" />
                LAUNCH TERMINAL
              </Button>
              <Button 
                onClick={() => {
                  import('../lib/analytics').then(m => m.analytics.ctaClick('hero_signup'));
                  navigate('/register');
                }}
                variant="outline" 
                className="border-white/20 text-white hover:bg-white/5 px-8 py-6 text-lg transition-all"
                data-testid="hero-signup"
              >
                Sign Up Free
              </Button>
            </div>
          </div>

          {/* Right side - Feature card */}
          <div className="relative">
            <div className="absolute -top-4 -right-4 w-64 h-64 bg-[#f2e2a6]/20 rounded-full blur-3xl" />
            
            <div className="relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <span className="text-gray-400 text-sm uppercase tracking-wider">Arbitrage Scanner</span>
              </div>

              <div className="text-7xl font-bold text-[#f2e2a6] mb-6">
                SCAN
              </div>

              <div className="flex items-center space-x-2 mb-4">
                <span className="px-3 py-1 rounded-full text-sm font-bold bg-[#f2e2a6]/20 text-[#f2e2a6] border border-[#f2e2a6]/50">
                  REAL-TIME
                </span>
                <span className="text-white text-sm font-mono">
                  Market Signals
                </span>
              </div>

              <h3 className="text-white text-lg font-medium mb-4">
                Find opportunities across prediction markets
              </h3>

              <div className="flex items-center space-x-4 mb-6">
                <div className="flex-1 bg-black/40 rounded-lg p-4">
                  <div className="text-gray-400 text-xs uppercase tracking-wider mb-1">PLATFORMS</div>
                  <div className="text-white text-sm font-semibold">Multiple</div>
                </div>
                <div className="flex-1 bg-black/40 rounded-lg p-4">
                  <div className="text-gray-400 text-xs uppercase tracking-wider mb-1">DATA</div>
                  <div className="text-[#f2e2a6] text-sm font-semibold">Live</div>
                </div>
              </div>

              <Button 
                onClick={() => navigate('/terminal')}
                className="w-full bg-[#f2e2a6] text-black hover:bg-[#f2e2a6]/90 font-semibold transition-all"
              >
                View Terminal
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
